import {
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  RadioButtonUnchecked,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';

import { VariantEditModalProps } from './types';
import { clampAdditionalUsers, getAdditionalUserOffer, getAdditionalUserTotalCents } from '../../../lib/services/additionalUsers';
import {
  getAvailableRecurringBoltOns,
  getRecurringBoltOnsTotalCents,
  normalizeRecurringBoltOnSelections,
} from '../../../lib/services/configuredAddons';
import type { MarketplaceService } from '../../../types/pricing';

const formatMonthly = (cents: number) => `$${(cents / 100).toFixed(2)}/mo`;

const getVariantBasePriceCents = (service: MarketplaceService, variantKey?: string | null, termMonths?: number) => {
  const variant = service.variants?.variants?.find((entry) => entry.key === variantKey);
  if (!variant) {
    return service.default_pricing?.recurring_cents || 0;
  }

  const override = variant.term_overrides?.find((entry) => entry.term_months === termMonths);
  return override?.recurring_cents ?? variant.pricing?.recurring_cents ?? 0;
};

const VariantEditModal: React.FC<VariantEditModalProps> = ({
  open,
  service,
  cartItem,
  selectedVariantKey,
  additionalUsers,
  recurringBoltOns,
  saving,
  onClose,
  onSave,
  onVariantChange,
  onAdditionalUsersChange,
  onRecurringBoltOnsChange,
}) => {
  const selectedVariant = useMemo(
    () => service?.variants?.variants?.find((variant) => variant.key === selectedVariantKey),
    [service, selectedVariantKey],
  );

  const additionalUsersEnabled = Boolean(
    service && selectedVariantKey && getAdditionalUserOffer(service, selectedVariantKey).enabled,
  );
  const additionalUserOffer = service && selectedVariantKey ? getAdditionalUserOffer(service, selectedVariantKey) : null;
  const quantityUnitLabel = additionalUserOffer?.unitLabel || 'user';
  const safeAdditionalUsers = additionalUsersEnabled
    ? clampAdditionalUsers(additionalUsers, additionalUserOffer?.maxQuantity)
    : 0;
  const quantityUnitLabelPlural = safeAdditionalUsers === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`;
  const basePriceCents = service ? getVariantBasePriceCents(service, selectedVariantKey, cartItem?.term_months) : 0;
  const additionalUsersTotalCents =
    service && selectedVariantKey ? getAdditionalUserTotalCents(service, selectedVariantKey, safeAdditionalUsers) : 0;

  const availableRecurringBoltOns = useMemo(
    () => (service ? getAvailableRecurringBoltOns(service, selectedVariantKey) : []),
    [service, selectedVariantKey],
  );
  const normalizedRecurringBoltOns = useMemo(
    () => normalizeRecurringBoltOnSelections(recurringBoltOns),
    [recurringBoltOns],
  );
  const recurringBoltOnsTotalCents =
    service && selectedVariantKey
      ? getRecurringBoltOnsTotalCents(service, selectedVariantKey, normalizedRecurringBoltOns)
      : 0;

  const totalPriceCents = basePriceCents + additionalUsersTotalCents + recurringBoltOnsTotalCents;

  const handleRecurringBoltOnToggle = (addonKey: string) => {
    const addon = availableRecurringBoltOns.find((entry) => entry.key === addonKey);
    if (!addon) return;

    const currentSelections = normalizeRecurringBoltOnSelections(normalizedRecurringBoltOns);
    const alreadySelected = currentSelections.some((selection) => selection.addon_key === addonKey);

    if (alreadySelected) {
      onRecurringBoltOnsChange(currentSelections.filter((selection) => selection.addon_key !== addonKey));
      return;
    }

    const nextSelections = addon.group
      ? currentSelections.filter((selection) => {
          const selectedAddon = availableRecurringBoltOns.find((entry) => entry.key === selection.addon_key);
          return selectedAddon?.group !== addon.group;
        })
      : currentSelections;

    onRecurringBoltOnsChange([...nextSelections, { addon_key: addonKey }]);
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: 520,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1.5 }}>
        <Box>
          <Typography variant="h6">Edit configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Update the plan and recurring add-ons before checkout.
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!service ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography color="text.secondary">Loading configuration…</Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Choose plan
              </Typography>
              <Stack spacing={1.5}>
                {service.variants?.variants?.map((variant) => {
                  const isSelected = variant.key === selectedVariantKey;
                  const includedUsers = getAdditionalUserOffer(service, variant.key).includedUsers;
                  const offerForVariant = getAdditionalUserOffer(service, variant.key);
                  const variantBasePriceCents = getVariantBasePriceCents(service, variant.key, cartItem?.term_months);

                  return (
                    <Card
                      key={variant.key}
                      variant="outlined"
                      sx={{
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                      }}
                    >
                      <CardActionArea onClick={() => onVariantChange(variant.key)}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {variant.display_name}
                                {isSelected && <CheckIcon color="primary" fontSize="small" />}
                              </Typography>
                              {variant.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {variant.description}
                                </Typography>
                              )}
                              {includedUsers > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  Includes {includedUsers} {includedUsers === 1 ? offerForVariant.unitLabel : `${offerForVariant.unitLabel}s`} · Extra {offerForVariant.unitLabel === 'user' ? 'users' : `${offerForVariant.unitLabel}s`} billed at {formatMonthly(offerForVariant.pricePerUserCents || 0)} each{offerForVariant.maxQuantity ? `, up to ${offerForVariant.maxQuantity}` : ''}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {formatMonthly(variantBasePriceCents)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  );
                })}
              </Stack>
            </Box>

            {additionalUsersEnabled && additionalUserOffer && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                  Recurring quantity add-on
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  {selectedVariant?.display_name || 'Selected plan'} includes {additionalUserOffer.includedUsers} {additionalUserOffer.includedUsers === 1 ? quantityUnitLabel : `${quantityUnitLabel}s`}. Add more at {formatMonthly(additionalUserOffer.pricePerUserCents)} each{additionalUserOffer.maxQuantity ? `, up to ${additionalUserOffer.maxQuantity}` : ''}.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <IconButton onClick={() => onAdditionalUsersChange(safeAdditionalUsers - 1)} disabled={saving || safeAdditionalUsers <= 0}>
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    label={`Extra ${quantityUnitLabelPlural}`}
                    type="number"
                    size="small"
                    value={safeAdditionalUsers}
                    onChange={(event) => onAdditionalUsersChange(clampAdditionalUsers(Number(event.target.value), additionalUserOffer.maxQuantity))}
                    inputProps={{ min: 0, step: 1, max: additionalUserOffer.maxQuantity ?? undefined }}
                    sx={{ width: 150 }}
                    disabled={saving}
                  />
                  <IconButton
                    onClick={() => onAdditionalUsersChange(safeAdditionalUsers + 1)}
                    disabled={saving || Boolean(additionalUserOffer.maxQuantity && safeAdditionalUsers >= additionalUserOffer.maxQuantity)}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Adds {formatMonthly(additionalUsersTotalCents)} to this line item.
                </Typography>
              </Box>
            )}

            {availableRecurringBoltOns.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700 }}>
                  Monthly options
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Choose one option per bolt-on group. Static IP blocks stay recurring and separate from one-time charges.
                </Typography>
                <Stack spacing={1.25}>
                  {availableRecurringBoltOns.map((addon) => {
                    const isSelected = normalizedRecurringBoltOns.some(
                      (selection) => selection.addon_key === addon.key,
                    );

                    return (
                      <Card
                        key={addon.key}
                        variant="outlined"
                        sx={{
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          borderWidth: isSelected ? 2 : 1,
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {addon.name}
                                {isSelected ? (
                                  <CheckIcon color="primary" fontSize="small" />
                                ) : (
                                  <RadioButtonUnchecked sx={{ fontSize: 18, color: 'text.secondary' }} />
                                )}
                              </Typography>
                              {addon.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {addon.description}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                {formatMonthly(addon.price_cents)} recurring{addon.group ? ` · group: ${addon.group}` : ''}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant={isSelected ? 'contained' : 'outlined'}
                              onClick={() => handleRecurringBoltOnToggle(addon.key)}
                              disabled={saving}
                            >
                              {isSelected ? 'Selected' : 'Select'}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">Plan</Typography>
                <Typography variant="body2">{formatMonthly(basePriceCents)}</Typography>
              </Box>
              {additionalUsersEnabled && safeAdditionalUsers > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">
                    {safeAdditionalUsers} extra {quantityUnitLabelPlural}
                  </Typography>
                  <Typography variant="body2">{formatMonthly(additionalUsersTotalCents)}</Typography>
                </Box>
              )}
              {recurringBoltOnsTotalCents > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Monthly options</Typography>
                  <Typography variant="body2">{formatMonthly(recurringBoltOnsTotalCents)}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.75, borderTop: '1px solid', borderColor: 'primary.100' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>New monthly total</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatMonthly(totalPriceCents)}</Typography>
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" disabled={!service || !selectedVariantKey || saving}>
          {saving ? 'Saving…' : 'Update cart'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantEditModal;
